import { GetServerSideProps } from "next";
import { Dimension } from "@dimensional-engineering/dimensional-models";
import Link from "next/link";

import styles from "../../styles/dimensions/infopage/DimensionsInfo.module.css";

import dimensinalJsonData from "../../assets/traitInfo/dimensions.json";
import Element from "@/components/traits/element/Element/Element";
import DimensionsLeftBar from "@/components/shared/dimensions/leftbar/DimensionsLeftBar";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import { iconMapper } from "@/components/shared/dimensions/icons/iconMapper";
import elementsJsonData from "../../assets/traitInfo/elements.json";
import patternsJsonData from "../../assets/traitInfo/patterns.json";
import archetypesJsonData from "../../assets/traitInfo/archetypes.json";
import {
  ExtendedScale,
  ExtendedPattern,
  ExtendedArchetype,
} from "@/models/sharedModels";
import TraitsAndDimensionsDownloadButton from "@/components/traits/shared/TraitsDownloadButton/TraitsAndDimensionsDownloadButton";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";

interface DimensionExtension extends Dimension {
  longDescription: string;
}

type DimensionInfoPageProps = {
  dimensionalData: DimensionExtension;
  elements: ExtendedScale[];
  patterns: ExtendedPattern[];
  archetypes: ExtendedArchetype[];
};

export default function DimensionInfoPage(props: DimensionInfoPageProps) {
  const { mobileSearchOpen } = usePublicProfileContext();
  const icons: any = iconMapper;
  const icon = icons[props.dimensionalData.slug];

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || ${props.dimensionalData.title}`}
        description={props.dimensionalData.longDescription}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div className={styles.main}>
        <div className={styles.top}>
          <BreadCrumbs
            list={["Dimensions", props.dimensionalData.title]}
            urls={["/dimensions", ""]}
          />
          <LargeTraitsHeading title={props.dimensionalData.title} />
        </div>
        <div className={styles.parent}>
          <DimensionsLeftBar
            icon={icon}
            dimensionalData={props.dimensionalData}
          />
          <div className={styles.rightBarMain}>
            <h3 className={styles.rightBarTitle}>
              Elements in {props.dimensionalData.title}
            </h3>
            <div className={styles.elementsWrapper}>
              <div className={styles.elementsDiv}>
                {props.elements.map((elem, index) => {
                  return (
                    <Link
                      href={`/traits/elements/${elem.slug}`}
                      key={index}
                      className="elBodyNoMarginMobile"
                    >
                      <Element slug={elem.slug} />
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className={styles.buttonDiv}>
              <TraitsAndDimensionsDownloadButton message="Find your score on over 100 traits on Dimensional." />
            </div>
          </div>
        </div>
      </div>
      <Footers />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let slug: any;
  let dataSlug: any;
  let dimensionalData;
  let elements: any[] = [];
  let patterns: any[] = [];
  let archetypes: any[] = [];

  if (ctx?.params?.slug) {
    slug = ctx.params.slug;
  }

  if (slug) {
    if (slug === "primary-traits") {
      dataSlug = "personality";
    } else if (slug === "cognition") {
      dataSlug = "personality-II";
    } else if (slug === "work-style") {
      dataSlug = "communication-style";
    } else {
      dataSlug = slug;
    }
  }

  if (dataSlug) {
    dimensinalJsonData.forEach((dim) => {
      if (dim.slug === dataSlug) {
        dimensionalData = dim;
      }
    });
    elementsJsonData.forEach((elem) => {
      if (elem.dimensionSlug === dataSlug && elem.isDiscoverable === true) {
        elements.push(elem);
      }
    });
    patternsJsonData.forEach((patt) => {
      if (
        patt.dimensionSlugs.includes(dataSlug) &&
        patt.isDiscoverable === true
      ) {
        patterns.push(patt);
      }
    });
    archetypesJsonData.forEach((arch) => {
      if (
        arch.dimensionSlugs.includes(dataSlug) &&
        arch.isDiscoverable === true
      ) {
        archetypes.push(arch);
      }
    });
  }

  if (dimensionalData === undefined) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      dimensionalData: dimensionalData,
      elements: elements,
      patterns: patterns,
      archetypes: archetypes,
    },
  };
};
